import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import PropTypes from "prop-types";
import { useMediaQuery } from "@react-hook/media-query";

import exists from "../../utils/exists";

// const ElephantFramingStatement = ({ setShowElephant, idea, auth }) => {
const ElephantFramingStatement = ({
  myStarIndex,
  data,
  showChangeLines,
  setSelected,
  updatedCount,
}) => {
  const smSize = useMediaQuery("only screen and (min-width: 600px)");
  const mdSize = useMediaQuery("only screen and (min-width: 768px)");
  const svgRef = useRef(null);

  const selectable = (index) => {
    if (data[index].myExpertiseLevel > 1) {
      return true;
    }
    if (data[index].children) {
      for (let k = 0; k < data[index].children.length; k += 1) {
        if (
          data[index].children[k].ideaType === 0 &&
          data[index].children[k].myPoint &&
          data[index].children[k].myPoint.trim().length > 0
        ) {
          return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    // Set the dimensions and margins of the graph
    const innerWidth = mdSize ? 595 : smSize ? 395 : 295;
    const innerHeight = 145; // Height = 200px
    const extraHeightBottom = 10; // 10 pixels are added on bottom so as not to chop off x-axis label
    const extraHeightTop = 25; // 25 pixels are added on bottom so as not to chop off x-axis label
    const margin = { top: 15, right: 15, bottom: 15, left: 15 };
    const width = innerWidth + margin.left + margin.right;
    const height = innerHeight + margin.top + margin.bottom + extraHeightBottom + extraHeightTop;

    const svgElement = d3.select(svgRef.current);
    svgElement.selectAll("*").remove(); // Clear svg content before adding new elements

    const svg = svgElement
      .attr("width", width)
      .attr("height", height)
      .attr("transform", `translate(${margin.left}, ${margin.bottom})`);

    // Y Axis
    const y = d3
      .scaleLinear()
      .domain([-1.15, 1.15])
      .range([innerHeight + extraHeightTop, extraHeightTop]); // axis height
    svg
      .append("g")
      .attr("transform", `translate(${innerWidth / 2},0)`)
      .call(d3.axisLeft(y).tickValues([]).tickSizeOuter(0));
    svg
      .append("text") // "Light" label

      .attr("y", 15)
      .attr("x", width / 2 - 15)
      .style("text-anchor", "middle")
      .text("Most Recent")
      .attr("font-size", "16");

    // X Axis
    const x = d3.scaleLinear().domain([-1.15, 1.15]).range([0, innerWidth]); // axis width
    svg
      .append("g")
      .attr("transform", `translate(0, ${innerHeight + extraHeightTop})`)
      .call(d3.axisBottom(x).tickSizeOuter(0).ticks(5));
    svg
      .append("text")
      .attr(
        "transform",
        `translate(${(mdSize ? -130 : smSize ? -85 : -45) + innerWidth / 2}, ${height - 5})`
      )
      .style("text-anchor", "middle")
      .text("Disagree")
      .attr("font-size", "16");

    svg
      .append("text")
      .attr(
        "transform",
        `translate(${(mdSize ? 130 : smSize ? 85 : 45) + innerWidth / 2}, ${height - 5})`
      )
      .style("text-anchor", "middle")
      .text("Agree")
      .attr("font-size", "16");

    const densityData = d3
      .contourDensity()
      .y((d) => {
        return y(d.y);
      })
      .x((d) => {
        return x(d.x);
      })
      .size([width, height])
      .bandwidth(smSize ? 30 : 20)(data);

    // Prepare a color palette
    const color = d3
      .scaleLinear()
      .domain([0, 0.65 * Math.max(...densityData.map((o) => o.value))]) // 0.65 provides darker colors on top (1.0 represenst max)
      .range(["white", "#0074F4"]);

    // Draw density image
    svg
      .selectAll("path")
      .data(densityData)
      .enter()
      .append("path")
      .attr("d", d3.geoPath())
      .attr("fill", (d) => {
        return color(d.value);
      });

    // Community member dots (unfilled)
    for (let j = 0; j < data.length; j += 1) {
      const Xpx = ((data[j].x + 1.15) / 2.3) * innerWidth;
      const Ypx = ((-data[j].y + 1.15) / 1.6) * innerHeight;
      if (myStarIndex !== j && !selectable(j)) {
        svg
          .append("circle")
          .attr("cx", Xpx)
          .attr("cy", Ypx)
          .attr("r", 3)
          // Extra space around the dot that is still active
          .attr("stroke-width", 1)
          .attr("stroke", "black")
          .style("fill", "white");
      }
    }

    // Community member dots (filled)
    for (let j = 0; j < data.length; j += 1) {
      const Xpx = ((data[j].x + 1.15) / 2.3) * innerWidth;
      const Ypx = ((-data[j].y + 1.15) / 1.6) * innerHeight;
      if (myStarIndex !== j && selectable(j)) {
        svg
          .append("circle")
          .attr("cx", Xpx)
          .attr("cy", Ypx)
          .attr("r", 3)
          // Extra space around the dot that is still active
          .attr("stroke-width", 1)
          .attr("stroke", "invisible")
          .style("fill", "black");
      }
    }

    // Change lines
    if (showChangeLines) {
      for (let j = 0; j < data.length; j += 1) {
        if (exists(data[j].x1) || exists(data[j].y1)) {
          const Xpx = ((data[j].x + 1.15) / 2.3) * innerWidth;
          const Ypx = ((-data[j].y + 1.15) / 1.6) * innerHeight;
          const X1px = (((exists(data[j].x1) ? data[j].x1 : data[j].x) + 1.15) / 2.3) * innerWidth;
          const Y1px =
            ((-(exists(data[j].y1) ? data[j].y1 : data[j].y) + 1.15) / 1.6) * innerHeight;

          svg
            .append("line")
            .attr("x1", X1px)
            .attr("y1", Y1px)
            .attr("x2", Xpx)
            .attr("y2", Ypx)
            .attr(
              "stroke",
              myStarIndex === j ? "yellow" : Xpx > X1px ? "green" : Xpx < X1px ? "red" : "black"
            )
            .attr("stroke-width", 4);
        }
      }
    }

    // My star
    if (myStarIndex >= 0) {
      const Xpx = ((data[myStarIndex].x + 1.15) / 2.3) * innerWidth;
      const Ypx = ((-data[myStarIndex].y + 1.15) / 1.6) * innerHeight;

      const sym = d3.symbol().type(d3.symbolStar).size(200);
      d3.select(svgRef.current)
        .append("path")
        .attr("d", sym)
        .attr("fill", "yellow")
        .attr("transform", `translate(${Xpx}, ${Ypx})`)
        .attr("stroke-width", 1)
        .attr("stroke", "black");
    }

    const goBrush = (e) => {
      const rows = [];
      for (let j = 0; j < data.length; j += 1) {
        const Xpx = ((data[j].x + 1.15) / 2.3) * innerWidth;
        const Ypx = ((-data[j].y + 1.15) / 1.6) * innerHeight;

        if (
          Xpx >= e.selection[0][0] &&
          Xpx <= e.selection[1][0] &&
          Ypx >= e.selection[0][1] &&
          Ypx <= e.selection[1][1] &&
          selectable(j)
        ) {
          rows.push(data[j]);
        }
      }
      setSelected(rows);
    };

    // Pause for half second before generating table
    let timer = null;
    const handleBrush = (e) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        goBrush(e);
      }, 500);
    };
    svg.append("g").call(d3.brush().on("brush", handleBrush));
  }, [smSize, mdSize, updatedCount, showChangeLines]);

  return <svg ref={svgRef} />;
};

ElephantFramingStatement.propTypes = {
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  myStarIndex: PropTypes.number.isRequired,
  showChangeLines: PropTypes.bool.isRequired,
  setSelected: PropTypes.func.isRequired,
  updatedCount: PropTypes.number.isRequired,
};

export default ElephantFramingStatement;
